<template>
  <div class="user-details-grid">
    <div class="header-container">
      <div class="system-type-filter">
        <SingleFilter
          :columns="filterColumns"
          :filter="filter"
          isStatic
          @filter-selected="handleFilterChange"
          @filter-emptied="handleFilterEmpty"
          @static-filter-cleared="handleFilterEmpty"
        ></SingleFilter>
      </div>
      <div class="date-time-container">
        <span class="timezone-text">{{timezoneText}}</span>
        <span class="date-selection-wrapper">
          <date-selection
            :min-date="minDate"
            :max-date="maxDate"
            :date-text="dateText"
            :value="dateValue"
            @range-selected="handleDateRangeUpdate"
          ></date-selection>
        </span>
      </div>
    </div>
    <div class="user-details-grid-panels-wrapper">
      <div class="user-details-grid-panel half-width">
        <grid-panel
          class="user-details-meetings-panel"
          :panelHeader="meetingsPanelHeaderTemplate"
          :loading="loading"
        >
          <user-details-trend-chart
            :categories="meetingsCategories"
            :chartSeries="meetingsChartSeries"
            :clickCallback="meetingsClickCallback"
            :labelClickCallback="meetingsLabelClickCallbackComputed"
            showLegend
          ></user-details-trend-chart>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel experience-score-panel half-width">
        <grid-panel
          panelHeader="Experience Score"
          :loading="loading"
        >
          <user-details-experience-score-panel
            :userExperienceScoreKpi="userExperienceScore.kpi"
            :userExperienceScoreTrend="userExperienceScore.trend"
          ></user-details-experience-score-panel>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel third-width">
        <grid-panel
          class="user-details-connection-quality-panel"
          panelTitle="Connection Quality"
          :loading="loading"
        >
          <user-details-pie-chart
            :chartSeries="connectionQualityChartSeries"
            :clickCallback="connectionQualityClickCallback"
          ></user-details-pie-chart>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel usage-history-panel full-width">
        <grid-panel
          class="user-details-usage-history-panel"
          panelHeader="Meeting History"
          :loading="loading"
        >
          <user-details-usage-history-chart
            :chartSeries="usageHistoryChartSeries"
            :clickCallback="usageHistoryClickCallback"
          ></user-details-usage-history-chart>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel connection-speed-panel third-width">
        <!-- TODO: Hide this panel when there is no data -->
        <grid-panel
          class="user-details-connection-speed-panel"
          :loading="loading"
        >
          <user-details-connection-speed-panel
            :wifi="connectionSpeed.wifi"
            :ethernet="connectionSpeed.ethernet"
            :cell="connectionSpeed.cell"
            :vpn="connectionSpeed.vpn"
          ></user-details-connection-speed-panel>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel half-width">
        <grid-panel
          class="user-details-meeting-peripherals-panel"
          panelHeader="Meeting Peripherals"
          :loading="loading"
        >
          <user-details-trend-chart
            :categories="meetingPeripheralsCategories"
            :chartSeries="meetingPeripheralsChartSeries"
            :clickCallback="meetingPeripheralsClickCallback"
          ></user-details-trend-chart>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel half-width">
        <grid-panel
          class="user-details-meeting-devices-panel"
          panelHeader="Meeting Devices"
          :loading="loading"
        >
          <user-details-trend-chart
            :categories="meetingDevicesCategories"
            :chartSeries="meetingDevicesChartSeries"
            :clickCallback="meetingDevicesClickCallback"
          ></user-details-trend-chart>
        </grid-panel>
      </div>
      <div class="user-details-grid-panel third-width">
        <grid-panel
          class="user-details-network-type-panel"
          panelTitle="Network Type"
          :loading="loading"
        >
          <user-details-pie-chart
            :chartSeries="networkTypeChartSeries"
            :clickCallback="networkTypeClickCallback"
          ></user-details-pie-chart>
        </grid-panel>
      </div>

    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import UserDetailsService from './user-details.service';
import DateSelectionPopup from '../../../vueComponents/dateSelectionPopup/dateSelectionPopup.vue';
import SingleFilter from '../../../vueComponents/singleFilter/src/singleFilter.vue';
import GridPanel from '../../../vueComponents/gridPanel.vue';
import UserDetailsTrendChart from './charts/userDetailsTrendChart.vue';
import UserDetailsExperienceScorePanel from './panels/userDetailsExperienceScorePanel.vue';
import UserDetailsPieChart from './charts/userDetailsPieChart.vue';
import UserDetailsConnectionSpeedPanel from './panels/userDetailsConnectionSpeedPanel.vue';
import UserDetailsUsageHistoryChart from './charts/userDetailsUsageHistoryChart.vue';
import UtilsService from "../../../services/utils.service";

export default {
  name: 'user-details-grid',
  components: {
    'date-selection': DateSelectionPopup,
    SingleFilter,
    GridPanel,
    UserDetailsTrendChart,
    UserDetailsExperienceScorePanel,
    UserDetailsPieChart,
    UserDetailsConnectionSpeedPanel,
    UserDetailsUsageHistoryChart
  },
  props: {
    userEmail: {
      type: String,
      default: ''
    },
    dateRange: {
      type: Object,
      default: () => {}
    },
    licenses: {
      type: Object,
      default: () => {}
    }
  },
  data: function() {
    return {
      timezoneText: UserDetailsService.getTimezoneText(),
      minDate: moment.max([
          UserDetailsService.getCurrentMoment().subtract(10, 'year').startOf('month'),
          moment("1-1-2022", "MM-DD-YYYY")
      ]).toDate(),
      maxDate: UserDetailsService.getCurrentMoment().endOf('day').toDate(),
      dateValue: UserDetailsService.getDefaultDateRange(),
      // TODO: Get data from API calls
      attendedCount: 0,
      hostedCount: 0,
      loading: true, // true by default so loading spinner shows on page load instead of "no data" message
      filter: {
        id: UtilsService.generateID(),
        field: "type",
        fieldText: "System Type",
        type: "string",
        filterComplete: false,
        knownValuePath: "/systems/types?bridgeOnly=true",
        valuesSubset: ['CISCOWEBEX', 'GMEET', 'MSTEAMS', 'ZOOM'],
        operatorText: "in",
        operatorValue: "=in=(|)"
      },
      filterColumns: [
        {
          title: "System Type",
          field: "type",
          filterType: "string",
          type: "STRING",
          knownValuePath: "/systems/types?bridgeOnly=true"
        }
      ],
      systemsList: undefined,
      connectionQualityChartSeries: [],
      connectionSpeed: {
        wifi: undefined,
        ethernet: undefined,
        cell: undefined,
        vpn: undefined
      },
      meetingDevicesChartSeries: [],
      meetingDevicesCategories: [],
      meetingPeripheralsChartSeries: [],
      meetingPeripheralsCategories: [],
      meetingsChartSeries: [],
      meetingsCategories: [],
      networkTypeChartSeries: [],
      usageHistoryChartSeries: [],
      userExperienceScore: {kpi: undefined, trend: undefined}
    }
  },
  computed: {
    meetingsPanelHeaderTemplate: function() {
      return '<div class="meetings-panel-header">' +
                '<span class="meetings-text">Meetings</span>' +
                '<span class="meetings-attended-count-container">' + this.attendedCount + ' <span class="meetings-count-text">Attended</span></span>' +
                '<span class="meetings-hosted-count-countainer">' + this.hostedCount + ' <span class="meetings-count-text">Hosted</span></span>' +
              '</div>';
    },
    dateText: function() {
      let dateFormat = 'MMM D, YYYY';
      return `${moment(this.dateValue.startDate).format(dateFormat)} - ${moment(this.dateValue.endDate).format(dateFormat)}`;
    },
    meetingsLabelClickCallbackComputed: function() {
      if (this.filter.filterComplete) {
        return undefined;
      }

      return this.meetingsLabelClickCallback;
    }
  },
  watch: {
    userEmail: function(newEmail, oldEmail) {
      if (newEmail) {
        this.fetchData();
      }
    },
    dateRange: {
      deep: true,
      handler(newDateRange, oldDateRange) {
        if (newDateRange.startDate && newDateRange.endDate) {
          this.dateValue.startDate = newDateRange.startDate;
          this.dateValue.endDate = newDateRange.endDate;
          this.fetchData();
        }
      }
    }
  },
  methods: {
    handleDateRangeUpdate: function(range) {
      this.updateUrl(range);
      this.dateValue = range;
    },
    handleFilterChange: function(filter) {
      this.filter = filter;
      this.systemsList = filter.queryVar ? filter.queryVar.split(',') : undefined;
      this.fetchData();
    },
    handleFilterEmpty: function() {
      if (this.filter) {
        delete this.filter.queryVar;
        this.filter.filterComplete = false;
        this.systemsList = undefined;
        this.fetchData();
      }
    },
    updateUrl: function(dateRangeObj) {
      let fullUrlRegex = new RegExp(/\/userdetails\/([-A-Za-z0-9]+)\/\d+\/\d+/),
          shortUrlRegex = new RegExp(/\/userdetails\/([-A-Za-z0-9]+)/),
          url = location.href,
          regex;

      if (fullUrlRegex.test(url)) {
        regex = fullUrlRegex;
      } else if (shortUrlRegex.test(url)) {
        regex = shortUrlRegex;
      }

      let match = url.match(regex);
      let userIdMatch;

      if (match && match[1]) {
        userIdMatch = match[1];
      }

      if (!userIdMatch) {
        throw new Error('Something went wrong updating the URL');
      }

      // Updating the URL will trigger the route subscription handler in user-details-view.js, which will propogate changes to the components
      let newUrl = url.replace(regex, '/userdetails/' + userIdMatch + '/' + moment(dateRangeObj.startDate).valueOf() + '/' + moment(dateRangeObj.endDate).valueOf());
      location.href = newUrl;
    },
    connectionQualityClickCallback: function(event) {
      UserDetailsService.connectionQualityClickCallback(this.userEmail, this.dateValue.startDate, this.dateValue.endDate, event, this.systemsList);
    },
    meetingDevicesClickCallback: function(event) {
      UserDetailsService.meetingDevicesClickCallback(this.userEmail, this.dateValue.startDate, this.dateValue.endDate, event, this.systemsList);
    },
    meetingPeripheralsClickCallback: function(event) {
      UserDetailsService.meetingPeripheralsClickCallback(this.userEmail, this.dateValue.startDate, this.dateValue.endDate, event, this.systemsList);
    },
    meetingsClickCallback: function(event) {
      UserDetailsService.meetingsClickCallback(this.userEmail, this.dateValue.startDate, this.dateValue.endDate, event)
    },
    meetingsLabelClickCallback: function(systemName) {
      if (systemName) {
        this.systemsList = [systemName];
        this.filter.filterComplete = true;
        // We use the spread operator to create a new object so that Vue detects the changes
        this.filter = {
          ...this.filter,
          queryVar: systemName,
          text: '' + systemName,
          value: 'type=in=("' + systemName + '")'
        };
        this.fetchData();
      }
    },
    networkTypeClickCallback: function(event) {
      UserDetailsService.networkTypeClickCallback(this.userEmail, this.dateValue.startDate, this.dateValue.endDate, event, this.systemsList);
    },
    usageHistoryClickCallback: function(event) {
      UserDetailsService.usageHistoryClickCallback(this.userEmail, this.dateValue.startDate, this.dateValue.endDate, event, this.systemsList);
    },
    fetchData: function() {
      let self = this,
          dateFormat = UserDetailsService.getDateFormat();

      if (self.userEmail && self.dateValue && self.dateValue.startDate && self.dateValue.endDate) {
        self.loading = true;
        UserDetailsService.getPanelsData(self.userEmail, self.dateValue.startDate.format(dateFormat), self.dateValue.endDate.format(dateFormat), self.systemsList)
          .then(result => {
            if (result.connectionQuality) {
              self.connectionQualityChartSeries = [{
                name: 'Quality',
                data: buildConnectionQualityData(result.connectionQuality)
              }];
            }

            if (result.connectionSpeed) {
              // special case for no data
              if (result.connectionSpeed.length === 1 &&
                  result.connectionSpeed[0].aggregateFact === 0 &&
                  result.connectionSpeed[0].connectiontype.toLowerCase() === 'average link speed (mbps)') {
                self.connectionSpeed.wifi = undefined;
                self.connectionSpeed.ethernet = undefined;
                self.connectionSpeed.cell = undefined;
                self.connectionSpeed.vpn = undefined;
              } else {
                for (let i = 0, _i = result.connectionSpeed.length; i < _i; i++) {
                  let dataPoint = result.connectionSpeed[i];
                  let val = dataPoint.aggregateFact + ' Mbps';
                  let quality = getLinkSpeedQuality(dataPoint.aggregateFact);
                  if (dataPoint.connectiontype) {
                    if (dataPoint.connectiontype.toLowerCase() === 'wifi') {
                      self.connectionSpeed.wifi = { val, quality };
                    }
                    if (dataPoint.connectiontype.toLowerCase() === 'ethernet' || dataPoint.connectiontype.toLowerCase() === 'wired') {
                      self.connectionSpeed.ethernet = { val, quality };
                    }
                    if (dataPoint.connectiontype.toLowerCase().includes('cellular')) {
                      self.connectionSpeed.cell = { val, quality };
                    }
                    if (dataPoint.connectiontype.toLowerCase() === 'vpn') {
                      self.connectionSpeed.vpn = { val, quality };
                    }
                  }
                }
              }
            }

            if(result.userExperienceScore) {
              self.userExperienceScore.trend = result.userExperienceScore;
            }
            if(result.userExperienceScoreKpi) {
              self.userExperienceScore.kpi = result.userExperienceScoreKpi[0].aggregateFact;
            }

            if (result.meetingDevices) {
              let devicesData = [],
                  devicesCategories = [];

              // detect special case for no data, only proceed if we have data
              if (!(result.meetingDevices.length === 1 &&
                    result.meetingDevices[0].aggregateFact === 0 &&
                    result.meetingDevices[0].device.toLowerCase() === 'participants (count)')) {

                for (let i = 0, _i = result.meetingDevices.length; i < _i; i++) {
                  let dataPoint = result.meetingDevices[i];
                  devicesData.push(dataPoint.aggregateFact);
                  // This conditional is from flexipanel-formatter.js to handle null values.
                  // It's unclear what the purpose or necessity of the '!== 0' check is.
                  if (!dataPoint.device && dataPoint.device !== 0) {
                    dataPoint.device = 'Unknown';
                  }
                  devicesCategories.push({
                    name: dataPoint.device//, TODO: Icon logic
                    //icon:
                  });
                }
                self.meetingDevicesCategories = devicesCategories;
                self.meetingDevicesChartSeries = [{
                  name: 'Meeting Device',
                  data: devicesData,
                  colorIndex: 26
                }];
              } else {
                self.meetingDevicesCategories = [];
                self.meetingDevicesChartSeries = [];
              }
            }

            if (result.meetingPeripherals) {
              let peripheralsData = [],
                  peripheralsCategories = [];

              // detect special case for no data, only proceed if we have data
              if (!(result.meetingPeripherals.length === 1 &&
                    result.meetingPeripherals[0].aggregateFact === 0 &&
                    result.meetingPeripherals[0].peripheral.toLowerCase() === 'peripheral count')) {

                for (let i = 0, _i = result.meetingPeripherals.length; i < _i; i++) {
                  let dataPoint = result.meetingPeripherals[i];
                  peripheralsData.push(dataPoint.aggregateFact);
                  peripheralsCategories.push({
                    name: dataPoint.peripheral//, TODO: Icon logic
                    //icon:
                  });
                }
                self.meetingPeripheralsCategories = peripheralsCategories;
                self.meetingPeripheralsChartSeries = [{
                  name: 'Meeting Peripherals',
                  data: peripheralsData,
                  colorIndex: 26
                }];
              } else {
                self.meetingPeripheralsCategories = [];
                self.meetingPeripheralsChartSeries = [];
              }
            }

            if (result.meetingsAttended && result.meetingsHosted) {
              let meetingsAttended = _.cloneDeep(result.meetingsAttended),
                  meetingsHosted = _.cloneDeep(result.meetingsHosted),
                  allMeetings = [],
                  attendedData = [],
                  hostedData = [],
                  meetingsCategories = [],
                  attendedCount = 0,
                  hostedCount = 0;

              // Add values to meetingsAttended in the case that the user is licensed for a platform but has no meetings on it.
              // Ignore this step if there is a complete filter, as we don't want to show results outside of the filter params.
              if (self.licenses && !self.filter.filterComplete) { // todo do we want to use something other than filterComplete here since we changed filters?
                // TODO: We need to know the exact string values for systemtype, not sure there's a better solution with the current API
                if (self.licenses.msteams && !meetingsAttended.find(meeting => meeting.systemtype === 'MSTEAMS')) {
                  meetingsAttended.push({
                    aggregateFact: 0,
                    systemtype: 'MSTEAMS'
                  });
                }
                if (self.licenses.webex && !meetingsAttended.find(meeting => meeting.systemtype === 'CISCOWEBEX')) {
                  meetingsAttended.push({
                    aggregateFact: 0,
                    systemtype: 'CISCOWEBEX'
                  });
                }
                if (self.licenses.gmeet && !meetingsAttended.find(meeting => meeting.systemtype === 'GMEET')) {
                  meetingsAttended.push({
                    aggregateFact: 0,
                    systemtype: 'GMEET'
                  });
                }
                if (self.licenses.zoom && !meetingsAttended.find(meeting => meeting.systemtype === 'ZOOM')) {
                  meetingsAttended.push({
                    aggregateFact: 0,
                    systemtype: 'ZOOM'
                  });
                }
              }

              // detect special case for no data, only proceed if we have data.
              // TODO: Is this still necessary? This bug may have been fixed, but leaving this code here shouldn't hurt.
              if (!(meetingsAttended.length === 1 &&
                    meetingsAttended[0].aggregateFact === 0 &&
                    meetingsAttended[0].systemtype.toLowerCase() === 'meetings attended count')) {

                for (let i = 0, _i = meetingsAttended.length; i < _i; i++) {
                  let dataPoint = meetingsAttended[i],
                      system = dataPoint.systemtype,
                      hostedMeeting = meetingsHosted.find(meeting => meeting.systemtype === system);

                  // We need to do this check again here in the case that a value was added to meetingsAttended in the above licenses logic.
                  // In such a case, the special case 'if' check will succeed, since meetingsAttended.length !== 1, but the junk 'meetings attended count'
                  // value will still exist in the meetingsAttended array. This should only occur when the user has 0 meetings and is licensed for at least 1 system.
                  // TODO: Is this still necessary? This bug may have been fixed, but leaving this code here shouldn't hurt.
                  if (dataPoint.systemtype.toLowerCase() === 'meetings attended count' && dataPoint.aggregateFact === 0) {
                    continue;
                  }

                  allMeetings.push({
                    system: system,
                    attended: dataPoint.aggregateFact,
                    hosted: hostedMeeting ? hostedMeeting.aggregateFact : 0
                  });
                }
              }

              allMeetings.sort(sortMeetings);

              for (let i = 0, _i = allMeetings.length; i < _i; i++) {
                let meeting = allMeetings[i];
                attendedData.push(meeting.attended);
                hostedData.push(meeting.hosted);
                attendedCount += meeting.attended;
                hostedCount += meeting.hosted;
                meetingsCategories.push({
                  name: meeting.system,
                  img: getMeetingTypeImage(meeting.system)
                });
              }

              self.attendedCount = attendedCount;
              self.hostedCount = hostedCount;
              self.meetingsCategories = meetingsCategories;
              self.meetingsChartSeries = [{
                name: 'Attended',
                data: attendedData,
                colorIndex: 25
              },{
                name: 'Hosted',
                data: hostedData,
                colorIndex: 26
              }];

              function getMeetingTypeImage(meetingType) {
                if (meetingType.toLowerCase().includes('msteams')) {
                  return 'dist/images/systems/20x20-ms-teams.png';
                }
                if (meetingType.toLowerCase().includes('webex')) {
                  return 'dist/images/systems/40x40-webex.png';
                }
                if (meetingType.toLowerCase().includes('gmeet')) {
                  return 'dist/images/systems/20x20-gmeet.png';
                }
                if (meetingType.toLowerCase().includes('zoom')) {
                  return 'dist/images/systems/40x40-zoom.png';
                }
              }

              function sortMeetings(a, b) {
                /* Descending sort. Example object:
                  {
                    system: MSTEAMS,
                    attended: 20,
                    hosted: 10
                  }
                */
                if (a.attended < b.attended) {
                  return 1;
                }
                if (a.attended > b.attended) {
                  return -1;
                }
                return 0;
              }
            }

            if (result.networkType) {
              self.networkTypeChartSeries = [{
                name: 'Network Type',
                data: buildNetworkTypeData(result.networkType)
              }];
            }

            if (result.usageHistory || result.usageMinutes || result.usageBadQuality) {
              let series = [];

              if (result.usageHistory) {
                let historyData = [];
                for (let i = 0, _i = result.usageHistory.length; i < _i; i++) {
                  let dataPoint = result.usageHistory[i];
                  if (dataPoint.periodStartDate && dataPoint.aggregateFact) {
                    historyData.push([
                      moment(dataPoint.periodStartDate).valueOf(),
                      dataPoint.aggregateFact
                    ]);
                  }
                }

                series.push({
                  name: 'Meetings',
                  type: 'line',
                  colorIndex: 27,
                  data: historyData
                })
              }

              if (result.usageMinutes) {
                let hoursData = [];
                for (let i = 0, _i = result.usageMinutes.length; i < _i; i++) {
                  let dataPoint = result.usageMinutes[i];
                  if (dataPoint.periodStartDate && dataPoint.aggregateFact) {
                    let hour = parseFloat(parseFloat(dataPoint.aggregateFact / 60).toFixed(2));
                    hoursData.push([
                      moment(dataPoint.periodStartDate).valueOf(),
                      hour
                    ]);
                  }
                }

                series.push({
                  name: 'Meeting Hours',
                  type: 'line',
                  data: hoursData
                })
              }

              if (result.usageBadQuality) {
                let badQualityData = [];
                for (let i = 0, _i = result.usageBadQuality.length; i < _i; i++) {
                  let dataPoint = result.usageBadQuality[i];
                  if (dataPoint.periodStartDate && dataPoint.aggregateFact) {
                    badQualityData.push([
                      moment(dataPoint.periodStartDate).valueOf(),
                      dataPoint.aggregateFact
                    ]);
                  }
                }

                series.push({
                  name: 'Quality Issues',
                  type: 'bar',
                  data: badQualityData
                })
              }

              self.usageHistoryChartSeries = series;
            }

            self.loading = false;

            function buildConnectionQualityData(connectionQualityArray) {
              let data = [];
              for (let i = 0, _i = connectionQualityArray.length; i < _i; i++) {
                let dataPoint = connectionQualityArray[i];
                let quality = dataPoint.qualityclass;
                // The API will return 1 record with value 0 and name "Participants (Count)" when there is no data.
                // We want to ignore that case, as well as "Unknown" values
                if (quality.toLowerCase() !== 'unknown' && quality.toLowerCase() !== 'participants (count)') {
                  data.push(Object.assign({
                    name: quality,
                    y: dataPoint.aggregateFact
                  }, getQualityColors(quality)));
                }
              }
              return data;
            }

            function buildNetworkTypeData(networkTypeArray) {
              let data = [];
              for (let i = 0, _i = networkTypeArray.length; i < _i; i++) {
                let dataPoint = networkTypeArray[i];
                let networkType = dataPoint.connectiontype;
                // The API will return 1 record with value 0 and name "Participants (Count)" when there is no data.
                // We want to ignore that case, as well as "Unknown" values
                if (networkType.toLowerCase() !== 'unknown' && networkType.toLowerCase() !== 'participants (count)') {
                  data.push({
                    name: networkType,
                    y: dataPoint.aggregateFact
                  });
                }
              }
              return data;
            }

            function getQualityColors(quality) {
              let colorsObj = {};
              if (quality.toLowerCase() === 'good') {
                colorsObj = {
                  colorIndex: 12
                };
              }
              if (quality.toLowerCase() === 'fair') {
                colorsObj = {
                  colorIndex: 11
                }
              }
              if (quality.toLowerCase() === 'bad') {
                colorsObj = {
                  colorIndex: 10
                }
              }
              return colorsObj;
            }

            function getLinkSpeedQuality(speed) {
              if (speed < 1.5) { return 'bad'; }
              if (speed < 100) { return 'fair'; }
              return 'good';
            }
          });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.user-details-grid {
  .header-container {
    padding: 26px 20px 20px 20px;
    display: flex;
    justify-content: space-between;
    min-height: 90px;
    background-color: var(--panel-background);
    border-radius: var(--border-radius);

    .system-type-filter {
      display: table-cell;
    }

    .date-time-container {
      display: table-cell;
      text-align: right;
      align-self: center;

      .timezone-text {
        font-size: 12px;
        margin-right: 10px;
      }
    }
  }

  .user-details-grid-panels-wrapper {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 20px 26px;
    padding-top: 20px;

    .full-width {
      grid-column: span 6;
    }
    .half-width {
      grid-column: span 3;
    }
    .third-width {
      grid-column: span 2;
    }
  }
  @media (max-width: 767px) {
    .user-details-grid-panels-wrapper {
      grid-template-columns: repeat(3, 1fr);

      .full-width {
        grid-column: span 3;
      }
    }
    .header-container {
      flex-direction: column;
      .date-time-container {
        text-align: left;
      }
    }
  }
  @media (max-width: 374px) {
    .user-details-grid-panels-wrapper {
      grid-template-columns: repeat(1, 100%);

      .full-width, .half-width, .third-width {
        grid-column: span 1;
      }
    }
  }
}
</style>
<style lang="scss">
.user-details-grid {
  .date-selection-wrapper {
    .dateSelection .custom-date-picker {
      padding: 10px;
      height: 40px;
      border-radius: var(--border-radius);
      border: 1px solid var(--secondary-btn-border-color);
      background-color: var(--secondary-btn-bg-color);
      display: inline-block;

      #dateRangePicker {
        right: 0;
      }
    }
  }

  .user-details-meetings-panel .grid-panel-header .meetings-panel-header {
    .meetings-text {
      margin-right: 20px;
    }

    .meetings-attended-count-container {
      margin-right: 20px;
    }

    .meetings-attended-count-container,
    .meetings-hosted-count-countainer {
      font-size: 16px;

      .meetings-count-text {
        font-weight: normal;
      }
    }
  }
}
</style>
